/**
 * Onglet « Ventes » — toutes les ventes immobilières enregistrées (DVF, depuis
 * 2014) autour d'une adresse, de la plus proche à la plus éloignée, dans un
 * rayon de 5 km maximum.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, TileLayer, Circle, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const API = '/api';
function headers() { return { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` }; }

interface Suggestion { label: string; city: string; postcode: string; citycode: string; type: string; lon: number; lat: number }

interface Vente {
  id: string;
  date: string;
  annee: number;
  valeur: number;
  nature: string;
  type: string;
  detail: string | null;
  surfaceHabitation: number | null;
  pieces: number | null;
  surfaceTerrain: number | null;
  prixM2: number | null;
  adresse: string | null;
  commune: string | null;
  parcelles: string[];
  composition?: Array<{ type: string; surface: number | null; pieces: number | null }>;
  cultures?: Array<{ nature: string; surface: number }>;
  lon: number;
  lat: number;
  distanceM: number;
}

interface Resultat {
  centre: { lon: number; lat: number; label: string | null };
  rayonKm: number;
  total: number;
  filtrees: number;
  periode: { de: number; a: number } | null;
  stats: Record<'Maison' | 'Appartement', { nombre: number; medianePrixM2: number | null }>;
  ventes: Vente[];
}

const TYPES = [
  { id: 'Maison', label: '🏠 Maisons', couleur: '#7c3aed' },
  { id: 'Appartement', label: '🏢 Appartements', couleur: '#0284c7' },
  { id: 'Terrain', label: '🌿 Terrains', couleur: '#059669' },
  { id: 'Autre', label: '🏪 Autres', couleur: '#d97706' },
];
const couleurType = (t: string) => (TYPES.find(x => x.id === t) || TYPES[3]).couleur;
const PERIODES = [
  { id: 0, label: 'Toutes' },
  { id: 2, label: '2 ans' },
  { id: 5, label: '5 ans' },
  { id: 10, label: '10 ans' },
];
const RAYONS = [0.5, 1, 2, 5];
const PAGE = 100;
type Tri = 'distance' | 'date' | 'prix' | 'prixM2' | 'surface' | 'terrain';

function EnTeteTri({ col, label, tri, onTri, droite = false, className = '' }: {
  col: Tri; label: string; tri: { col: Tri; ordre: 'asc' | 'desc' }; onTri: (c: Tri) => void; droite?: boolean; className?: string;
}) {
  const actif = tri.col === col;
  return (
    <th className={`py-2.5 font-medium ${droite ? 'text-right' : ''} ${className}`}>
      <button
        onClick={() => onTri(col)}
        title={actif ? (tri.ordre === 'asc' ? 'Trié par ordre croissant — cliquer pour décroissant' : 'Trié par ordre décroissant — cliquer pour croissant') : 'Trier'}
        className={`inline-flex items-center gap-1 hover:text-violet-700 ${actif ? 'text-violet-700 font-semibold' : ''}`}
      >
        {label}
        <span className={`text-[10px] ${actif ? '' : 'opacity-30'}`}>{actif ? (tri.ordre === 'asc' ? '▲' : '▼') : '▲▼'}</span>
      </button>
    </th>
  );
}

const prix = (v: number) => v.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' €';
const dateFr = (d: string) => { const x = new Date(d); return isNaN(x.getTime()) ? d : x.toLocaleDateString('fr-FR'); };
const distance = (m: number) => (m < 1000 ? `${m} m` : `${(m / 1000).toFixed(m < 10000 ? 1 : 0).replace('.', ',')} km`);

function Recentrer({ lat, lon, rayonKm }: { lat: number; lon: number; rayonKm: number }) {
  const map = useMap();
  useEffect(() => {
    const d = rayonKm / 111.32;
    map.fitBounds([[lat - d, lon - d * 1.45], [lat + d, lon + d * 1.45]]);
  }, [lat, lon, rayonKm, map]);
  return null;
}

const ETAPES = [
  "📍 Localisation de l'adresse",
  '🗺️ Communes dans le rayon',
  '💰 Ventes récentes (DVF)',
  '🗄️ Ventes depuis 2014',
  '📏 Calcul des distances',
  '📝 Classement des résultats',
];

function Chargement() {
  const [e, setE] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setE(x => Math.min(x + 1, ETAPES.length - 1)), 1600);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" role="status" aria-live="polite">
      <div className="flex items-center gap-3">
        <span className="w-5 h-5 rounded-full border-2 border-violet-200 border-t-violet-600 animate-spin shrink-0" />
        <div>
          <p className="text-sm font-semibold text-gray-900">Chargement des ventes…</p>
          <p key={e} className="text-xs text-violet-600">{ETAPES[e]}</p>
        </div>
      </div>
      <div className="mt-4 h-1 rounded-full bg-violet-100 overflow-hidden">
        <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 animate-pulse" />
      </div>
      <p className="text-[11px] text-gray-400 mt-3">
        La première recherche sur un secteur peut prendre jusqu'à une minute (positionnement des ventes anciennes) — les suivantes sont quasi instantanées.
      </p>
    </div>
  );
}

const LIBELLES_TRI: Record<Tri, string> = {
  distance: 'Distance', date: 'Date', prix: 'Prix', prixM2: 'Prix au m²', surface: 'Surface habitable', terrain: 'Terrain',
};

const refParcelle = (idu: string) => `${idu.slice(8, 10).replace(/^0/, '')} ${idu.slice(10).replace(/^0+/, '')}`;

function Info({ label, valeur }: { label: string; valeur: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-50 last:border-b-0 text-sm">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="font-medium text-gray-900 text-right">{valeur}</span>
    </div>
  );
}

// Détail d'une vente : plein écran sur mobile, fenêtre centrée sur ordinateur.
function ModalVente({ vente: v, centre, onFermer }: { vente: Vente; centre: { lon: number; lat: number }; onFermer: () => void }) {
  useEffect(() => {
    const echap = (e: KeyboardEvent) => e.key === 'Escape' && onFermer();
    document.addEventListener('keydown', echap);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', echap); document.body.style.overflow = overflow; };
  }, [onFermer]);

  const lienStreetView = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${v.lat},${v.lon}`;
  const lienMaps = `https://www.google.com/maps/search/?api=1&query=${v.lat},${v.lon}`;
  const lienGeoportail = `https://www.geoportail.gouv.fr/carte?c=${v.lon},${v.lat}&z=18&l0=ORTHOIMAGERY.ORTHOPHOTOS::GEOPORTAIL:OGC:WMTS(1)&l1=CADASTRALPARCELS.PARCELLAIRE_EXPRESS::GEOPORTAIL:OGC:WMTS(1)&permalink=yes`;

  // Rendu dans <body> (portail) pour passer au-dessus de toute l'interface, et
  // hauteur en « dvh » : sur mobile, « vh » inclut la zone cachée sous la barre
  // d'adresse du navigateur — le haut du panneau (et la croix) s'y retrouvait.
  return createPortal(
    <div className="fixed inset-0 z-[5000] flex items-end sm:items-center justify-center bg-black/40" onClick={onFermer}>
      <div
        role="dialog"
        aria-modal="true"
        onClick={e => e.stopPropagation()}
        className="evo-modal-vente bg-white w-full sm:max-w-lg flex flex-col rounded-t-3xl sm:rounded-2xl shadow-xl"
      >
        {/* 85vh pour les anciens navigateurs, 85dvh (zone réellement visible) sinon */}
        <style>{`.evo-modal-vente { max-height: 85vh; max-height: 85dvh; }`}</style>
        <div className="shrink-0 bg-white rounded-t-3xl sm:rounded-t-2xl px-5 pt-2 pb-3 border-b border-gray-100">
          <div className="sm:hidden mx-auto mb-2 h-1 w-10 rounded-full bg-gray-200" />
          <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-gray-500">Vente du {dateFr(v.date)}{v.nature !== 'Vente' ? ` · ${v.nature}` : ''}</p>
            <p className="text-2xl font-bold text-gray-900 leading-tight">{prix(v.valeur)}</p>
            {v.prixM2 && <p className="text-sm font-semibold text-violet-700">{v.prixM2.toLocaleString('fr-FR')} €/m²</p>}
          </div>
          <button onClick={onFermer} aria-label="Fermer" className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 text-lg hover:bg-gray-200 shrink-0">✕</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-gray-50 px-2 py-2.5">
              <p className="text-[11px] text-gray-500">Bien</p>
              <p className="text-sm font-bold text-gray-900 flex items-center justify-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ background: couleurType(v.type) }} />{v.type}
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 px-2 py-2.5">
              <p className="text-[11px] text-gray-500">Habitable</p>
              <p className="text-sm font-bold text-gray-900">{v.surfaceHabitation ? `${v.surfaceHabitation} m²` : '—'}</p>
            </div>
            <div className="rounded-xl bg-gray-50 px-2 py-2.5">
              <p className="text-[11px] text-gray-500">Terrain</p>
              <p className="text-sm font-bold text-gray-900">{v.surfaceTerrain ? `${v.surfaceTerrain.toLocaleString('fr-FR')} m²` : '—'}</p>
            </div>
          </div>

          <div>
            <Info label="Adresse" valeur={<>{v.adresse || '—'}<span className="block text-xs text-gray-400 font-normal">{v.commune}</span></>} />
            <Info label="Distance" valeur={`${distance(v.distanceM)} de l'adresse recherchée`} />
            {v.pieces ? <Info label="Pièces principales" valeur={v.pieces} /> : null}
            {v.detail && <Info label="Composition" valeur={v.detail} />}
            {v.composition && v.composition.length > 0 && (
              <Info
                label="Détail des locaux"
                valeur={v.composition.map((c, i) => (
                  <span key={i} className="block">
                    {c.type}{c.surface ? ` · ${c.surface} m²` : ''}{c.pieces ? ` · ${c.pieces} p.` : ''}
                  </span>
                ))}
              />
            )}
            {v.cultures && v.cultures.length > 0 && (
              <Info
                label="Nature du terrain"
                valeur={v.cultures.map((c, i) => (
                  <span key={i} className="block">{c.nature} · {c.surface.toLocaleString('fr-FR')} m²</span>
                ))}
              />
            )}
            <Info
              label={v.parcelles.length > 1 ? `Parcelles (${v.parcelles.length})` : 'Parcelle'}
              valeur={v.parcelles.length ? v.parcelles.map(p => <span key={p} className="block">{refParcelle(p)} <span className="text-xs text-gray-400 font-normal">({p})</span></span>) : '—'}
            />
            <Info label="Type de mutation" valeur={v.nature} />
          </div>

          <div className="rounded-xl overflow-hidden border border-gray-100 h-48">
            <MapContainer center={[v.lat, v.lon]} zoom={17} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
              <TileLayer
                attribution="© IGN"
                url="https://data.geopf.fr/wmts?LAYER=ORTHOIMAGERY.ORTHOPHOTOS&EXCEPTIONS=text/xml&FORMAT=image/jpeg&SERVICE=WMTS&VERSION=1.0.0&REQUEST=GetTile&STYLE=normal&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}"
                maxZoom={19}
              />
              <CircleMarker center={[v.lat, v.lon]} radius={9} pathOptions={{ color: '#fff', weight: 2, fillColor: couleurType(v.type), fillOpacity: 1 }} />
              <CircleMarker center={[centre.lat, centre.lon]} radius={6} pathOptions={{ color: '#fff', weight: 2, fillColor: '#111827', fillOpacity: 1 }}>
                <Tooltip>Adresse recherchée</Tooltip>
              </CircleMarker>
            </MapContainer>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <a href={lienStreetView} target="_blank" rel="noopener noreferrer" className="text-center text-xs font-semibold px-2 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50">🚶 Street View</a>
            <a href={lienMaps} target="_blank" rel="noopener noreferrer" className="text-center text-xs font-semibold px-2 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50">📍 Google Maps</a>
            <a href={lienGeoportail} target="_blank" rel="noopener noreferrer" className="text-center text-xs font-semibold px-2 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50">🗺️ Cadastre IGN</a>
          </div>

          <p className="text-[11px] text-gray-400 leading-relaxed">
            Source : Demandes de valeurs foncières (DGFiP). Le prix inclut l'ensemble des biens vendus ensemble (dépendances, terrains).
            {v.annee < new Date().getFullYear() - 5 ? ' Position approximative : centre de la parcelle cadastrale.' : ''}
          </p>
        </div>

        <div className="shrink-0 border-t border-gray-100 px-5 pt-3" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
          <button onClick={onFermer} className="w-full py-3 rounded-xl bg-gray-900 text-white text-sm font-semibold">Fermer</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function VentesPage() {
  const [adresse, setAdresse] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [voirSuggestions, setVoirSuggestions] = useState(false);
  const ignorerSuggest = useRef(false);
  const [point, setPoint] = useState<{ lon: number; lat: number; label: string } | null>(null);

  const [rayon, setRayon] = useState(5);
  const [types, setTypes] = useState<string[]>([]);
  const [periode, setPeriode] = useState(0);
  const [tri, setTri] = useState<{ col: Tri; ordre: 'asc' | 'desc' }>({ col: 'distance', ordre: 'asc' });
  // 1er clic : croissant · 2e clic : décroissant
  const trier = (col: Tri) => setTri(t => (t.col === col ? { col, ordre: t.ordre === 'asc' ? 'desc' : 'asc' } : { col, ordre: 'asc' }));

  const [res, setRes] = useState<Resultat | null>(null);
  const [ventes, setVentes] = useState<Vente[]>([]);
  const [chargement, setChargement] = useState(false);
  const [chargementSuite, setChargementSuite] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [survol, setSurvol] = useState<string | null>(null);
  const [detail, setDetail] = useState<Vente | null>(null);
  const lignesRef = useRef<Record<string, HTMLTableRowElement | null>>({});

  // Autocomplétion d'adresse (même service que l'onglet Cadastre)
  useEffect(() => {
    if (ignorerSuggest.current) { ignorerSuggest.current = false; return; }
    if (adresse.trim().length < 3) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/cadastre/suggest?q=${encodeURIComponent(adresse.trim())}`, { headers: headers() });
        const d = await r.json();
        if (d.success) { setSuggestions(d.suggestions); setVoirSuggestions(true); }
      } catch { /* confort uniquement */ }
    }, 300);
    return () => clearTimeout(t);
  }, [adresse]);

  const anneeMin = periode ? new Date().getFullYear() - periode : null;

  async function charger(offset: number, p = point) {
    const params = new URLSearchParams({ rayon: String(rayon), offset: String(offset), limit: String(PAGE) });
    if (p) { params.set('lon', String(p.lon)); params.set('lat', String(p.lat)); params.set('label', p.label); }
    else params.set('address', adresse.trim());
    if (types.length) params.set('types', types.join(','));
    if (anneeMin) params.set('anneeMin', String(anneeMin));
    params.set('tri', tri.col);
    params.set('ordre', tri.ordre);
    const r = await fetch(`${API}/ventes?${params}`, { headers: headers() });
    const d = await r.json();
    if (!r.ok || !d.success) throw new Error(d.error?.message || d.message || d.error || 'Recherche échouée');
    return d as Resultat;
  }

  async function rechercher(p = point) {
    if (!p && !adresse.trim()) return;
    setChargement(true);
    setErreur(null);
    setVoirSuggestions(false);
    try {
      const d = await charger(0, p);
      setRes(d);
      setVentes(d.ventes);
      if (!p) setPoint({ lon: d.centre.lon, lat: d.centre.lat, label: d.centre.label || adresse });
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Erreur réseau');
      setRes(null);
      setVentes([]);
    }
    setChargement(false);
  }

  // Filtres : relance immédiate (le serveur garde le calcul en cache)
  useEffect(() => {
    if (point) rechercher(point);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rayon, types.join(','), periode, tri.col, tri.ordre]);

  async function afficherPlus() {
    setChargementSuite(true);
    try {
      const d = await charger(ventes.length);
      setVentes(v => [...v, ...d.ventes]);
    } catch { /* on garde ce qui est affiché */ }
    setChargementSuite(false);
  }

  function choisir(s: Suggestion) {
    ignorerSuggest.current = true;
    setAdresse(s.label);
    setSuggestions([]);
    setVoirSuggestions(false);
    const p = { lon: s.lon, lat: s.lat, label: s.label };
    setPoint(p);
    rechercher(p);
  }

  const basculerType = (t: string) => setTypes(x => (x.includes(t) ? x.filter(y => y !== t) : [...x, t]));

  const ventesCarte = useMemo(() => ventes.slice(0, 500), [ventes]);

  return (
    <div className="max-w-[1800px] mx-auto space-y-5">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-base font-semibold text-gray-900">💰 Ventes autour d'une adresse</h2>
        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
          Toutes les ventes enregistrées par l'administration fiscale (DVF) depuis 2014, de la plus proche à la plus éloignée.
        </p>
        <form
          onSubmit={e => { e.preventDefault(); setPoint(null); rechercher(null); }}
          className="mt-4 flex gap-2 flex-wrap"
        >
          <div className="relative flex-1 min-w-[260px]">
            <input
              value={adresse}
              onChange={e => { setAdresse(e.target.value); setPoint(null); }}
              onFocus={() => suggestions.length && setVoirSuggestions(true)}
              onBlur={() => setTimeout(() => setVoirSuggestions(false), 150)}
              placeholder="Ex. 460 Route d'Oyeu, Charavines"
              className="w-full px-4 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-200"
            />
            {voirSuggestions && suggestions.length > 0 && (
              <div className="absolute z-[1000] mt-1 w-full bg-white rounded-xl border border-gray-100 shadow-lg overflow-hidden">
                {suggestions.map((s, i) => (
                  <button
                    type="button"
                    key={i}
                    onMouseDown={() => choisir(s)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-violet-50 border-b border-gray-50 last:border-b-0"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={chargement || !adresse.trim()}
            className="px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-semibold rounded-xl hover:from-violet-700 hover:to-indigo-700 shadow-sm disabled:opacity-60"
          >
            {chargement ? (
              <span className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />Recherche…
              </span>
            ) : 'Rechercher'}
          </button>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500 mr-1">Rayon</span>
            {RAYONS.map(r => (
              <button key={r} onClick={() => setRayon(r)}
                className={`px-2.5 py-1 rounded-full border font-semibold ${rayon === r ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                {r < 1 ? `${r * 1000} m` : `${r} km`}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-gray-500 mr-1">Type</span>
            {TYPES.map(t => (
              <button key={t.id} onClick={() => basculerType(t.id)}
                className={`px-2.5 py-1 rounded-full border font-semibold ${types.includes(t.id) ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500 mr-1">Période</span>
            {PERIODES.map(p => (
              <button key={p.id} onClick={() => setPeriode(p.id)}
                className={`px-2.5 py-1 rounded-full border font-semibold ${periode === p.id ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {erreur && <p className="text-sm text-red-500 mt-3">❌ {erreur}</p>}
      </div>

      {chargement && !res && <Chargement />}

      {detail && res && <ModalVente vente={detail} centre={res.centre} onFermer={() => setDetail(null)} />}

      {res && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm px-4 py-3">
              <p className="text-xs text-gray-500">Ventes trouvées</p>
              <p className="text-xl font-bold text-gray-900">{res.filtrees.toLocaleString('fr-FR')}</p>
              <p className="text-[11px] text-gray-400">dans {res.rayonKm < 1 ? `${res.rayonKm * 1000} m` : `${res.rayonKm} km`}{res.periode ? ` · ${res.periode.de}–${res.periode.a}` : ''}</p>
            </div>
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm px-4 py-3">
              <p className="text-xs text-gray-500">Prix médian maison</p>
              <p className="text-xl font-bold text-violet-700">{res.stats.Maison.medianePrixM2 ? `${res.stats.Maison.medianePrixM2.toLocaleString('fr-FR')} €/m²` : '—'}</p>
              <p className="text-[11px] text-gray-400">{res.stats.Maison.nombre} vente(s)</p>
            </div>
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm px-4 py-3">
              <p className="text-xs text-gray-500">Prix médian appartement</p>
              <p className="text-xl font-bold text-sky-700">{res.stats.Appartement.medianePrixM2 ? `${res.stats.Appartement.medianePrixM2.toLocaleString('fr-FR')} €/m²` : '—'}</p>
              <p className="text-[11px] text-gray-400">{res.stats.Appartement.nombre} vente(s)</p>
            </div>
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm px-4 py-3">
              <p className="text-xs text-gray-500">Adresse de référence</p>
              <p className="text-sm font-semibold text-gray-900 leading-snug">{res.centre.label || adresse}</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-[1fr_420px] gap-5 items-start">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Mobile : tri + cartes compactes (l'essentiel), détail au toucher */}
              <div className="md:hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
                  <span className="text-xs text-gray-500">Trier par</span>
                  <select
                    value={tri.col}
                    onChange={e => setTri({ col: e.target.value as Tri, ordre: 'asc' })}
                    className="flex-1 text-sm rounded-lg border border-gray-200 px-2 py-1.5 bg-white"
                  >
                    {(Object.keys(LIBELLES_TRI) as Tri[]).map(c => <option key={c} value={c}>{LIBELLES_TRI[c]}</option>)}
                  </select>
                  <button
                    onClick={() => setTri(t => ({ ...t, ordre: t.ordre === 'asc' ? 'desc' : 'asc' }))}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-700"
                  >
                    {tri.ordre === 'asc' ? '▲ Croissant' : '▼ Décroissant'}
                  </button>
                </div>
                <ul className="divide-y divide-gray-50">
                  {ventes.map(v => (
                    <li key={v.id}>
                      <button onClick={() => setDetail(v)} className="w-full text-left px-4 py-3 active:bg-violet-50">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs text-gray-500">{dateFr(v.date)} · {distance(v.distanceM)}</p>
                            <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 mt-0.5">
                              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: couleurType(v.type) }} />
                              {v.type}{v.surfaceHabitation ? ` · ${v.surfaceHabitation} m²` : ''}{v.pieces ? ` · ${v.pieces} p.` : ''}
                            </p>
                            <p className="text-xs text-gray-400 truncate mt-0.5">{v.adresse || '—'}{v.commune ? `, ${v.commune}` : ''}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-base font-bold text-gray-900">{prix(v.valeur)}</p>
                            {v.prixM2 && <p className="text-xs text-violet-700">{v.prixM2.toLocaleString('fr-FR')} €/m²</p>}
                            <p className="text-gray-300 text-sm mt-0.5">›</p>
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50/80">
                    <tr className="text-left text-xs text-gray-500">
                      <EnTeteTri col="distance" label="Distance" tri={tri} onTri={trier} className="pl-4" />
                      <EnTeteTri col="date" label="Date" tri={tri} onTri={trier} />
                      <th className="py-2.5 font-medium">Adresse</th>
                      <th className="py-2.5 font-medium">Bien</th>
                      <EnTeteTri col="surface" label="Habitable" tri={tri} onTri={trier} droite />
                      <EnTeteTri col="terrain" label="Terrain" tri={tri} onTri={trier} droite />
                      <EnTeteTri col="prix" label="Prix" tri={tri} onTri={trier} droite />
                      <EnTeteTri col="prixM2" label="€/m²" tri={tri} onTri={trier} droite className="pr-4" />
                    </tr>
                  </thead>
                  <tbody>
                    {ventes.map(v => (
                      <tr
                        key={v.id}
                        ref={el => { lignesRef.current[v.id] = el; }}
                        onMouseEnter={() => setSurvol(v.id)}
                        onMouseLeave={() => setSurvol(null)}
                        onClick={() => setDetail(v)}
                        className={`border-t border-gray-50 transition-colors cursor-pointer ${survol === v.id ? 'bg-violet-50' : 'hover:bg-gray-50'}`}
                      >
                        <td className="py-2 pl-4 font-semibold text-gray-900 whitespace-nowrap">{distance(v.distanceM)}</td>
                        <td className="py-2 text-gray-600 whitespace-nowrap">{dateFr(v.date)}</td>
                        <td className="py-2 text-gray-900">
                          <span className="block leading-tight">{v.adresse || '—'}</span>
                          <span className="text-xs text-gray-400">{v.commune}</span>
                        </td>
                        <td className="py-2">
                          <span className="inline-flex items-center gap-1.5 font-medium text-gray-900">
                            <span className="w-2 h-2 rounded-full" style={{ background: couleurType(v.type) }} />
                            {v.type}{v.pieces ? ` · ${v.pieces} p.` : ''}
                          </span>
                          {v.nature !== 'Vente' && <span className="block text-[11px] text-amber-600">{v.nature}</span>}
                        </td>
                        <td className="py-2 text-right text-gray-700 whitespace-nowrap">{v.surfaceHabitation ? `${v.surfaceHabitation} m²` : '—'}</td>
                        <td className="py-2 text-right text-gray-700 whitespace-nowrap">{v.surfaceTerrain ? `${v.surfaceTerrain.toLocaleString('fr-FR')} m²` : '—'}</td>
                        <td className="py-2 text-right font-semibold text-gray-900 whitespace-nowrap">{prix(v.valeur)}</td>
                        <td className="py-2 pr-4 text-right text-gray-600 whitespace-nowrap">{v.prixM2 ? v.prixM2.toLocaleString('fr-FR') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {ventes.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-10">Aucune vente avec ces critères dans ce rayon.</p>
              )}
              {ventes.length < res.filtrees && (
                <div className="border-t border-gray-100 p-3 text-center">
                  <button onClick={afficherPlus} disabled={chargementSuite}
                    className="px-4 py-2 text-xs font-semibold rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                    {chargementSuite ? 'Chargement…' : `Afficher plus (${(res.filtrees - ventes.length).toLocaleString('fr-FR')} restantes)`}
                  </button>
                </div>
              )}
            </div>

            <div className="lg:sticky lg:top-4 space-y-2">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-[460px]">
                <MapContainer center={[res.centre.lat, res.centre.lon]} zoom={13} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
                  <TileLayer
                    attribution="© IGN — Plan IGN"
                    url="https://data.geopf.fr/wmts?LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&EXCEPTIONS=text/xml&FORMAT=image/png&SERVICE=WMTS&VERSION=1.0.0&REQUEST=GetTile&STYLE=normal&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}"
                    maxZoom={19}
                  />
                  <Recentrer lat={res.centre.lat} lon={res.centre.lon} rayonKm={res.rayonKm} />
                  <Circle center={[res.centre.lat, res.centre.lon]} radius={res.rayonKm * 1000} pathOptions={{ color: '#7c3aed', weight: 1.5, fillOpacity: 0.04 }} />
                  <CircleMarker center={[res.centre.lat, res.centre.lon]} radius={7} pathOptions={{ color: '#111827', fillColor: '#111827', fillOpacity: 1 }}>
                    <Tooltip>{res.centre.label || 'Adresse recherchée'}</Tooltip>
                  </CircleMarker>
                  {ventesCarte.map(v => (
                    <CircleMarker
                      key={v.id}
                      center={[v.lat, v.lon]}
                      radius={survol === v.id ? 8 : 5}
                      pathOptions={{ color: '#fff', weight: 1, fillColor: couleurType(v.type), fillOpacity: survol === v.id ? 1 : 0.8 }}
                      eventHandlers={{
                        mouseover: () => setSurvol(v.id),
                        mouseout: () => setSurvol(null),
                        click: () => setDetail(v),
                      }}
                    >
                      <Tooltip>
                        <strong>{prix(v.valeur)}</strong> · {dateFr(v.date)}<br />
                        {v.type}{v.surfaceHabitation ? ` · ${v.surfaceHabitation} m²` : ''} — {distance(v.distanceM)}
                      </Tooltip>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>
              <div className="flex flex-wrap gap-3 px-1 text-xs text-gray-500">
                {TYPES.map(t => (
                  <span key={t.id} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: t.couleur }} />{t.label.replace(/^\S+\s/, '')}</span>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 px-1 leading-relaxed">
                Source : Demandes de valeurs foncières (DGFiP), publiées deux fois par an avec environ 6 mois de décalage.
                Les ventes antérieures à {new Date().getFullYear() - 5} sont placées au centre de leur parcelle cadastrale.
                Le prix au m² n'est calculé que pour les ventes d'un seul logement.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
